import { useEffect, useMemo, useState } from "react";
import { findUniqueProjectByCode } from "../../project-context.js";
import "../../dwb/dwb.css";
import { AppShell } from "../components/AppShell";
import { api } from "../lib/api";
import {
  dwbApi,
  type DwbCard,
  type DwbDryRunAttempt,
  type DwbIssue,
  type DwbOperationResult,
  type DwbWeekAdapter,
  type DwbWeekRun,
  type DwbWeekSource,
} from "../lib/dwbApi";
import {
  canManageDwb,
  currentIsoWeek,
  parseDwbSelectionArtifact,
  reviewSummary,
  RUN_STATUS_LABELS,
  runAdapterIsStale,
  runSourceIsStale,
  shiftIsoWeek,
} from "../lib/dwbModel";

const AMS11_PROJECT_CODE = "ams11";
const ISO_WEEK = /^\d{4}-W\d{2}$/;

function operationKey(prefix: string): string {
  const suffix = globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pwa-dwb-${prefix}-${suffix}`;
}

function formatDate(isoDate: string | undefined): string {
  if (!isoDate) return "—";
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function textValue(value: unknown, fallback = "—"): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function uniqueEmployeeCount(source: DwbWeekSource | null): number {
  const ids = new Set<string>();
  for (const day of source?.days ?? []) {
    for (const employee of day?.employees ?? []) {
      if (employee?.project_employee_id) ids.add(employee.project_employee_id);
    }
  }
  return ids.size;
}

function activityList(adapter: DwbWeekAdapter | null): string[] {
  const values = new Set<string>();
  for (const day of adapter?.draft_week_input?.days ?? []) {
    for (const activity of day?.activities ?? []) {
      if (activity) values.add(activity);
    }
  }
  return [...values].sort((left, right) => left.localeCompare(right, "nl"));
}

function statusClass(status: string): string {
  return ["draft", "selected", "reviewed", "ready", "submitted", "failed"]
      .includes(status)
    ? status
    : "draft";
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`dwb-status dwb-status--${statusClass(status)}`}>
      {RUN_STATUS_LABELS[status] ?? status ?? "Onbekend"}
    </span>
  );
}

function ReviewBadge({ status }: { status?: string }) {
  if (status === "approved") {
    return <span className="dwb-chip dwb-chip--ok">Goedgekeurd</span>;
  }
  if (status === "rejected") {
    return <span className="dwb-chip dwb-chip--danger">Afgekeurd</span>;
  }
  return <span className="dwb-chip dwb-chip--info">Nog beoordelen</span>;
}

function IssueList(
  { issues, kind }: { issues: DwbIssue[]; kind: "blocking" | "warning" },
) {
  if (!issues.length) return null;
  return (
    <div className="dwb-issues">
      {issues.map((issue, index) => {
        const meta = [
          issue.date ? formatDate(issue.date) : null,
          issue.employee_name ?? null,
          issue.source_text ?? null,
        ].filter((value): value is string => Boolean(value));
        return (
          <div
            className={`dwb-issue dwb-issue--${kind}`}
            key={`${issue.code ?? kind}-${index}`}
          >
            <strong>
              {issue.code ??
                (kind === "blocking" ? "Blokkade" : "Waarschuwing")}
            </strong>
            <div>{issue.message ?? ""}</div>
            {meta.length > 0 && <div className="dwb-issue__meta">{meta.join(" · ")}</div>}
          </div>
        );
      })}
    </div>
  );
}

function acceptedResult(
  result: DwbOperationResult,
  acceptedCodes: string[],
): DwbOperationResult {
  if (!result || result.status !== "accepted") {
    throw new Error(
      `Actie niet uitgevoerd (${
        result?.code ?? "onbekende_fout"
      }). Herlaad de week en probeer opnieuw.`,
    );
  }
  if (acceptedCodes.length > 0 && !acceptedCodes.includes(result.code ?? "")) {
    throw new Error(`Onverwacht resultaat: ${result.code ?? "onbekend"}.`);
  }
  return result;
}

function acceptedRun(
  result: DwbOperationResult,
  acceptedCodes: string[],
): DwbWeekRun {
  const accepted = acceptedResult(result, acceptedCodes);
  if (!accepted.run) {
    throw new Error("De DWB-actie gaf geen bijgewerkte weekrun terug.");
  }
  return accepted.run;
}

export function DwbPage() {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectLabel, setProjectLabel] = useState("AMS11");
  const [mayManage, setMayManage] = useState(false);
  const [week, setWeek] = useState(currentIsoWeek());
  const [source, setSource] = useState<DwbWeekSource | null>(null);
  const [adapter, setAdapter] = useState<DwbWeekAdapter | null>(null);
  const [run, setRun] = useState<DwbWeekRun | null>(null);
  const [dryRun, setDryRun] = useState<DwbOperationResult | null>(null);
  const [selectionFile, setSelectionFile] = useState<File | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [resetReason, setResetReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadWeekData(
    targetProjectId: string,
    targetWeek: string,
  ): Promise<void> {
    if (!ISO_WEEK.test(targetWeek)) {
      throw new Error("Kies een geldige ISO-week.");
    }
    setError(null);
    setBusy(true);
    setDryRun(null);
    try {
      const [nextSource, nextAdapter, runs] = await Promise.all([
        dwbApi.getWeekSource(targetProjectId, targetWeek),
        dwbApi.getWeekAdapter(targetProjectId, targetWeek),
        dwbApi.listWeekRuns(targetProjectId, 100),
      ]);
      const summary = (runs ?? []).find((item) => item?.iso_week === targetWeek) ?? null;
      const nextRun = summary?.dwb_week_run_id
        ? await dwbApi.getWeekRun(summary.dwb_week_run_id)
        : null;
      setSource(nextSource);
      setAdapter(nextAdapter);
      setRun(nextRun);
      setReviewNotes({});
      setResetReason("");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const context = await api.getAccessContext();
        if (!active) return;
        const project = findUniqueProjectByCode(context, AMS11_PROJECT_CODE);
        if (!project?.project_id) {
          throw new Error(
            "AMS11 is niet eenduidig beschikbaar voor deze gebruiker.",
          );
        }
        const initialWeek = currentIsoWeek();
        setProjectId(project.project_id);
        setProjectLabel(
          project.display_name || project.project_code || "AMS11",
        );
        setMayManage(canManageDwb(context, project.project_id));
        setWeek(initialWeek);
        await loadWeekData(project.project_id, initialWeek);
      } catch (cause) {
        if (active) {
          setError(cause instanceof Error ? cause.message : "Onbekende fout");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const sourceStale = useMemo(() => runSourceIsStale(run, source), [
    run,
    source,
  ]);
  const adapterStale = useMemo(() => runAdapterIsStale(run, adapter), [
    run,
    adapter,
  ]);
  const stale = sourceStale || adapterStale;
  const summary = useMemo(() => reviewSummary(run?.cards), [run]);
  const activities = useMemo(() => activityList(adapter), [adapter]);

  async function perform(action: () => Promise<void>): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Onbekende fout");
    } finally {
      setBusy(false);
    }
  }

  function switchWeek(nextWeek: string): void {
    setWeek(nextWeek);
    if (!projectId) return;
    void loadWeekData(projectId, nextWeek).catch((cause) => {
      setError(cause instanceof Error ? cause.message : "Onbekende fout");
    });
  }

  async function createRun(): Promise<void> {
    if (!projectId || !source || !adapter) {
      throw new Error("De DWB-weekbron is nog niet geladen.");
    }
    const result = await dwbApi.createWeekRun(
      projectId,
      week,
      source,
      adapter,
      operationKey(`create-${week}`),
    );
    if (result.code === "active_week_run_exists" && result.run) {
      setRun(result.run);
      return;
    }
    setRun(acceptedRun(result, ["dwb_week_run_created"]));
  }

  async function refreshRun(): Promise<void> {
    if (!run || !source || !adapter) {
      throw new Error("De DWB-weekrun is nog niet geladen.");
    }
    setRun(acceptedRun(
      await dwbApi.refreshWeekRun(
        run.dwb_week_run_id,
        run.revision,
        source,
        adapter,
        operationKey(`refresh-${week}`),
      ),
      ["dwb_week_run_refreshed", "dwb_week_run_unchanged"],
    ));
  }

  async function importSelection(): Promise<void> {
    if (!run) throw new Error("Maak eerst een DWB-weekrun aan.");
    if (!selectionFile) {
      throw new Error(
        "Kies eerst het JSON-bestand van de canonieke DWB-selector.",
      );
    }
    if (selectionFile.size > 2_000_000) {
      throw new Error(
        "Het selectie-artifact is onverwacht groot; maximum is 2 MB.",
      );
    }
    const parsed = parseDwbSelectionArtifact(await selectionFile.text(), week);
    setRun(acceptedRun(
      await dwbApi.setWeekSelection(
        run.dwb_week_run_id,
        run.revision,
        parsed.selectorVersion,
        parsed.selectorCommit,
        parsed.selectionSeed,
        parsed.cards,
        operationKey(`selection-${week}`),
      ),
      ["dwb_selection_set"],
    ));
    setSelectionFile(null);
  }

  async function reviewCard(
    card: DwbCard,
    decision: "approved" | "rejected",
  ): Promise<void> {
    if (!run) throw new Error("De DWB-weekrun is nog niet geladen.");
    const note = (reviewNotes[card.dwb_card_id] ?? card.review_note ?? "")
      .trim();
    if (decision === "rejected" && !note) {
      throw new Error("Vul bij afkeuren een korte reden in.");
    }
    setRun(acceptedRun(
      await dwbApi.reviewCard(
        card.dwb_card_id,
        run.revision,
        decision,
        note || null,
        operationKey(`review-${card.dwb_card_id}`),
      ),
      ["dwb_card_reviewed", "dwb_card_review_unchanged"],
    ));
  }

  async function resetSelection(): Promise<void> {
    if (!run) throw new Error("De DWB-weekrun is nog niet geladen.");
    const reason = resetReason.trim();
    if (!reason) {
      throw new Error("Vul een reden in voordat je de selectie reset.");
    }
    setRun(acceptedRun(
      await dwbApi.resetWeekSelection(
        run.dwb_week_run_id,
        run.revision,
        reason,
        operationKey(`reset-${run.selection_revision ?? 0}`),
      ),
      ["dwb_selection_reset"],
    ));
    setResetReason("");
    setDryRun(null);
  }

  async function markReady(): Promise<void> {
    if (!run) throw new Error("De DWB-weekrun is nog niet geladen.");
    setRun(acceptedRun(
      await dwbApi.markWeekReady(
        run.dwb_week_run_id,
        run.revision,
        operationKey(`ready-${run.selection_revision ?? 0}`),
      ),
      ["dwb_week_ready"],
    ));
    setDryRun(null);
  }

  async function prepareFormsDryRun(): Promise<void> {
    if (!run) throw new Error("De DWB-weekrun is nog niet geladen.");
    setDryRun(acceptedResult(
      await dwbApi.prepareFormsDryRun(
        run.dwb_week_run_id,
        run.revision,
        operationKey(`forms-dry-run-${run.selection_revision ?? 0}`),
      ),
      ["dwb_forms_dry_run_prepared"],
    ));
  }

  function renderRunActions() {
    if (!run) return null;
    if (!mayManage) {
      return (
        <p className="dwb-muted">
          Je hebt alleen leesrechten voor deze DWB-week.
        </p>
      );
    }

    if (run.status === "draft") {
      if (stale) {
        return (
          <div className="dwb-run-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => void perform(refreshRun)}
            >
              Bron en adapter verversen
            </button>
          </div>
        );
      }
      if (!run.adapter_ready) {
        return (
          <div className="dwb-alert dwb-alert--warning">
            Los eerst de blockers in de dagrapportbron op. Daarna kan deze concept-run worden
            ververst.
          </div>
        );
      }
      return (
        <div className="dwb-import">
          <div className="dwb-alert">
            De weekcontext is gereed. Importeer het JSON-artifact van de canonieke{" "}
            <strong>selection-v1</strong> selector. Ongepinde lokale output wordt geweigerd.
          </div>
          <label className="dwb-label" htmlFor="dwb-selection-file">
            Selector JSON
          </label>
          <input
            id="dwb-selection-file"
            type="file"
            accept="application/json,.json"
            disabled={busy}
            onChange={(event) => setSelectionFile(event.target.files?.[0] ?? null)}
          />
          <div className="dwb-run-actions">
            <button
              type="button"
              disabled={busy || !selectionFile}
              onClick={() => void perform(importSelection)}
            >
              Selectie importeren
            </button>
          </div>
        </div>
      );
    }

    if (run.status === "selected" || run.status === "reviewed") {
      const resetNeeded = stale || summary.rejected > 0;
      const mayReady = run.status === "reviewed" && summary.pending === 0 &&
        summary.rejected === 0 && !stale;
      return (
        <>
          {resetNeeded && (
            <div className="dwb-reset">
              <input
                type="text"
                maxLength={2000}
                value={resetReason}
                placeholder="Reden voor nieuwe selectie"
                disabled={busy}
                onChange={(event) => setResetReason(event.target.value)}
              />
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => void perform(resetSelection)}
              >
                Selectie resetten
              </button>
            </div>
          )}
          {mayReady && (
            <div className="dwb-run-actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => void perform(markReady)}
              >
                Markeer week als gereed
              </button>
            </div>
          )}
        </>
      );
    }

    if (run.status === "ready") {
      return (
        <>
          <div className="dwb-alert dwb-alert--success">
            <strong>Gereed.</strong>{" "}
            De kaarten zijn beoordeeld. Een Forms dry-run maakt alleen een auditbare preview en
            verstuurt niets.
          </div>
          <div className="dwb-run-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => void perform(prepareFormsDryRun)}
            >
              Maak Microsoft Forms dry-run
            </button>
          </div>
        </>
      );
    }
    return null;
  }

  const blockers = adapter?.blockers ?? [];
  const warnings = adapter?.warnings ?? [];
  const cards = run?.cards ?? [];
  const dryRunAttempts: DwbDryRunAttempt[] = dryRun?.attempts ?? [];
  const mayReview = Boolean(
    mayManage && run && !stale &&
      (run.status === "selected" || run.status === "reviewed"),
  );

  return (
    <AppShell>
      <main className="panel">
        <div className={`dwb-page${busy ? " dwb-loading" : ""}`}>
          <div>
            <h1>Dont Walk By</h1>
            <p className="dwb-muted">
              {projectLabel} · auditbare weekselectie en review
            </p>
          </div>

          <section className="dwb-toolbar" aria-label="DWB week kiezen">
            <div className="dwb-toolbar__field">
              <label htmlFor="dwb-week">ISO-week</label>
              <input
                id="dwb-week"
                type="week"
                value={week}
                disabled={busy || !projectId}
                onChange={(event) => setWeek(event.target.value)}
              />
            </div>
            <div className="dwb-toolbar__nav">
              <button
                type="button"
                className="secondary"
                disabled={busy || !projectId}
                onClick={() => switchWeek(shiftIsoWeek(week, -1))}
              >
                Vorige
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy || !projectId}
                onClick={() => switchWeek(currentIsoWeek())}
              >
                Huidige
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy || !projectId}
                onClick={() => switchWeek(shiftIsoWeek(week, 1))}
              >
                Volgende
              </button>
            </div>
            <button
              type="button"
              disabled={busy || !projectId || !ISO_WEEK.test(week)}
              onClick={() => {
                if (!projectId) return;
                void loadWeekData(projectId, week).catch((cause) =>
                  setError(
                    cause instanceof Error ? cause.message : "Onbekende fout",
                  )
                );
              }}
            >
              Week laden
            </button>
          </section>

          {loading && (
            <section className="dwb-section">
              <p>DWB-week laden...</p>
            </section>
          )}
          {error && (
            <div className="dwb-alert dwb-alert--danger" role="alert">
              <strong>Fout:</strong> {error}
            </div>
          )}

          {!loading && source && adapter && (
            <section className="dwb-section">
              <div className="dwb-heading-row">
                <h2>Weekbron</h2>
                {adapter.ready
                  ? (
                    <span className="dwb-chip dwb-chip--ok">
                      Context compleet
                    </span>
                  )
                  : (
                    <span className="dwb-chip dwb-chip--danger">
                      Review nodig
                    </span>
                  )}
              </div>
              <div className="dwb-summary-grid">
                <div className="dwb-summary-item">
                  <span className="dwb-label">Dagrapporten</span>
                  <strong>{source.days?.length ?? 0}</strong>
                </div>
                <div className="dwb-summary-item">
                  <span className="dwb-label">Medewerkers</span>
                  <strong>{uniqueEmployeeCount(source)}</strong>
                </div>
                <div className="dwb-summary-item">
                  <span className="dwb-label">Activiteiten</span>
                  <strong>{activities.join(", ") || "—"}</strong>
                </div>
                <div className="dwb-summary-item">
                  <span className="dwb-label">Blockers</span>
                  <strong>{blockers.length}</strong>
                </div>
              </div>
              {(source.days?.length ?? 0) === 0 && (
                <div className="dwb-alert dwb-alert--warning">
                  Voor deze week zijn nog geen dagrapporten gevonden.
                </div>
              )}
              <IssueList issues={blockers} kind="blocking" />
              <IssueList issues={warnings} kind="warning" />
            </section>
          )}

          {!loading && source && adapter && !run && (
            <section className="dwb-section">
              <div className="dwb-heading-row">
                <h2>Weekrun</h2>
                <span className="dwb-status dwb-status--draft">
                  Nog niet vastgelegd
                </span>
              </div>
              <p>
                Er is voor {week} nog geen operationele DWB-weekrun opgeslagen.
              </p>
              {mayManage
                ? (
                  <div className="dwb-run-actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void perform(createRun)}
                    >
                      Weekrun vastleggen{adapter.ready ? "" : " met huidige blockers"}
                    </button>
                  </div>
                )
                : <p className="dwb-muted">Je hebt alleen leesrechten.</p>}
            </section>
          )}

          {!loading && run && (
            <section className="dwb-section">
              <div className="dwb-heading-row">
                <h2>Weekrun {run.iso_week}</h2>
                <StatusBadge status={run.status} />
              </div>
              <div className="dwb-summary-grid">
                <div className="dwb-summary-item">
                  <span className="dwb-label">Run-revisie</span>
                  <strong>{run.revision ?? 0}</strong>
                </div>
                <div className="dwb-summary-item">
                  <span className="dwb-label">Selectierevisie</span>
                  <strong>{run.selection_revision ?? 0}</strong>
                </div>
                <div className="dwb-summary-item">
                  <span className="dwb-label">Kaarten</span>
                  <strong>{summary.total}</strong>
                </div>
                <div className="dwb-summary-item">
                  <span className="dwb-label">Review</span>
                  <strong>
                    {summary.approved} akkoord · {summary.rejected} afkeur · {summary.pending} open
                  </strong>
                </div>
              </div>
              {stale && (
                <div className="dwb-alert dwb-alert--danger">
                  <strong>Snapshot verouderd.</strong>{" "}
                  {sourceStale && "De dagrapportbron is gewijzigd. "}
                  {adapterStale &&
                    "De adapter/library-pin of normalisatie is gewijzigd. "}
                  {run.status === "draft"
                    ? "Ververs de concept-run voordat je verdergaat."
                    : run.status === "selected" || run.status === "reviewed"
                    ? "Review/ready is geblokkeerd. Reset eerst de selectie en ververs daarna de run."
                    : "Deze afgeronde run blijft gekoppeld aan zijn oorspronkelijke snapshot."}
                </div>
              )}
              {run.selector_commit && (
                <p className="dwb-muted">
                  Selector {run.selector_version ?? ""} · {run.selector_commit} · seed{" "}
                  {run.selection_seed ?? "—"}
                </p>
              )}
              {renderRunActions()}
            </section>
          )}

          {cards.length > 0 && (
            <div className="dwb-cards">
              {cards.map((card) => (
                <article
                  className={`dwb-card dwb-card--${card.review_status ?? "pending"}`}
                  key={card.dwb_card_id}
                >
                  <div className="dwb-card__title">
                    <div>
                      <h3>{card.employee_name ?? "Onbekende medewerker"}</h3>
                      <div className="dwb-muted">
                        Kaart {card.selection_order ?? "—"} · {formatDate(card.selected_date)}
                      </div>
                    </div>
                    <ReviewBadge status={card.review_status} />
                  </div>
                  <div className="dwb-card__meta">
                    <span className="dwb-chip dwb-chip--info">
                      {card.observation_type ?? "—"}
                    </span>
                    <span className="dwb-chip dwb-chip--info">
                      {card.category ?? "—"}
                    </span>
                    <span className="dwb-chip dwb-chip--info">
                      {card.activity_type ?? "—"}
                    </span>
                  </div>
                  <p className="dwb-card__observation">
                    {card.observation_text ?? ""}
                  </p>
                  <div className="dwb-muted">Entry: {card.entry_id ?? "—"}</div>
                  {mayReview
                    ? (
                      <>
                        <label
                          className="dwb-label"
                          htmlFor={`note-${card.dwb_card_id}`}
                        >
                          Reviewnotitie (verplicht bij afkeuren)
                        </label>
                        <textarea
                          id={`note-${card.dwb_card_id}`}
                          maxLength={2000}
                          value={reviewNotes[card.dwb_card_id] ??
                            card.review_note ?? ""}
                          disabled={busy}
                          onChange={(event) =>
                            setReviewNotes((notes) => ({
                              ...notes,
                              [card.dwb_card_id]: event.target.value,
                            }))}
                        />
                        <div className="dwb-card__actions">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void perform(() => reviewCard(card, "approved"))}
                          >
                            Goedkeuren
                          </button>
                          <button
                            type="button"
                            className="secondary"
                            disabled={busy}
                            onClick={() => void perform(() => reviewCard(card, "rejected"))}
                          >
                            Afkeuren
                          </button>
                        </div>
                      </>
                    )
                    : card.reviewed_at
                    ? (
                      <div className="dwb-muted">
                        Beoordeeld: {formatDateTime(card.reviewed_at)}
                        {card.review_note ? ` · ${card.review_note}` : ""}
                      </div>
                    )
                    : null}
                </article>
              ))}
            </div>
          )}

          {dryRun && (
            <section className="dwb-section">
              <div className="dwb-heading-row">
                <h2>Microsoft Forms dry-run</h2>
                <span className="dwb-chip dwb-chip--ok">
                  Geen externe submit
                </span>
              </div>
              <div className="dwb-alert dwb-alert--success">
                Preview opgeslagen voor {dryRunAttempts.length}{" "}
                kaart{dryRunAttempts.length === 1 ? "" : "en"}. Er is geen request naar Microsoft
                Forms verstuurd.
              </div>
              <div className="dwb-cards">
                {dryRunAttempts.map((attempt, index) => {
                  const fields = attempt.request_payload?.fields ?? {};
                  return (
                    <article className="dwb-card" key={index}>
                      <div className="dwb-card__title">
                        <h3>
                          {textValue(fields.naam, "Onbekende medewerker")}
                        </h3>
                        <span className="dwb-chip dwb-chip--info">Dry-run</span>
                      </div>
                      <div className="dwb-card__meta">
                        <span className="dwb-chip dwb-chip--info">
                          {textValue(fields.type_observatie)}
                        </span>
                        <span className="dwb-chip dwb-chip--info">
                          {textValue(fields.categorie)}
                        </span>
                        <span className="dwb-chip dwb-chip--info">
                          {textValue(fields.datum)}
                        </span>
                      </div>
                      <p className="dwb-card__observation">
                        {textValue(fields.omschrijving, "")}
                      </p>
                      <div className="dwb-muted">
                        Bedrijf: {textValue(fields.bedrijf)} · Locatie: {textValue(fields.locatie)}
                        {" "}
                        · Interventie: {textValue(fields.interventie)}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
    </AppShell>
  );
}
